/**
 * A store read completed after its owner/auth/request lease stopped being
 * current. Callers must retry it; treating this as a successful empty load can
 * turn a harmless account transition into a false-empty Journal redirect.
 */
export class ContentHydrationStaleError extends Error {
  readonly code = 'content-hydration-stale';

  constructor(reason: string) {
    super(`content hydration became stale: ${reason}`);
    this.name = 'ContentHydrationStaleError';
  }
}

export type ContentHydrationSource = 'prayers' | 'bookmarks' | 'highlights';

let publishProbe: ((source: ContentHydrationSource) => Promise<void>) | null = null;
let joinedLoadProbe: ((source: ContentHydrationSource) => void) | null = null;

/** Probe-only deterministic pause immediately before final publication guards. */
export function __setContentHydrationPublishProbe(
  probe: ((source: ContentHydrationSource) => Promise<void>) | null,
): void {
  publishProbe = probe;
}

/** Probe-only witness that an identical-context caller joined an active read. */
export function __setContentHydrationJoinedLoadProbe(
  probe: ((source: ContentHydrationSource) => void) | null,
): void {
  joinedLoadProbe = probe;
}

export async function runContentHydrationPublishProbe(
  source: ContentHydrationSource,
): Promise<void> {
  await publishProbe?.(source);
}

export function recordContentHydrationJoinedLoad(source: ContentHydrationSource): void {
  joinedLoadProbe?.(source);
}
